// Safe auth interceptor plugin: wraps $fetch to retry once on 401 by calling /api/auth/refresh
import {
  defineNuxtPlugin,
  useNuxtApp,
  navigateTo,
  useRuntimeConfig,
} from "#imports";
import { useAuthModal } from "~/composables/auth/useAuthModal";

export default defineNuxtPlugin(() => {
  const nuxt = useNuxtApp();

  const maybeNuxtFetch = (nuxt as any)?.$fetch;
  const maybeGlobalFetch = (globalThis as any)?.$fetch;

  // Fallback minimal fetch wrapper that throws on non-2xx so our logic works
  const nativeFallback = async (input: any, init?: any) => {
    const res = await fetch(input as any, init as any);
    if (!res.ok) {
      const err: any = new Error("Fetch error");
      err.statusCode = res.status;
      throw err;
    }
    const ct = res.headers.get("content-type") || "";
    if (ct.includes("application/json")) return res.json();
    return res.text();
  };

  const originalFetch: any =
    maybeNuxtFetch && typeof maybeNuxtFetch === "function"
      ? maybeNuxtFetch.bind(nuxt)
      : maybeGlobalFetch && typeof maybeGlobalFetch === "function"
        ? maybeGlobalFetch
        : nativeFallback;

  let refreshing: Promise<any> | null = null;

  async function doRefresh() {
    if (!refreshing) {
      // Call backend refresh endpoint directly (use runtime config to get api base)
      const cfg = useRuntimeConfig();
      const pub = (cfg.public || {}) as any;
      const base = String(pub.apiBase || "");
      const refreshUrl = base
        ? `${base.replace(/\/$/, "")}/auth/refresh`
        : "/api/auth/refresh";
      refreshing = originalFetch(refreshUrl, {
        method: "POST",
        credentials: "include",
      })
        .then((r: any) => {
          refreshing = null;
          return r;
        })
        .catch((e: any) => {
          refreshing = null;
          throw e;
        });
    }
    return refreshing;
  }

  const wrapper = async (input: any, init?: any) => {
    // Check if credentials are explicitly set to 'omit' - if so, don't retry on 401
    const credentialsOmitted = init?.credentials === "omit";

    try {
      return await originalFetch(input, init);
    } catch (err: any) {
      // AbortError is thrown by Nuxt's useAsyncData deduplication and by
      // explicit AbortController signals — neither are auth errors.
      if (err?.name === "AbortError") {
        throw err;
      }

      const status =
        err?.status ||
        err?.statusCode ||
        err?.response?.status ||
        err?.data?.statusCode;

      // Check if this is an email verification required error (403)
      const errorData = err?.data || err?.response?.data || {};
      const errorCode = errorData?.error || errorData?.statusCode;
      const errorMessage = errorData?.message || err?.message || "";

      if (
        status === 403 ||
        errorCode === "EMAIL_VERIFICATION_REQUIRED" ||
        errorMessage.includes("Email verification required")
      ) {
        // Don't redirect automatically - let the middleware handle it for protected routes
        // This allows unverified users to browse public pages
        // Just throw the error so calling code knows verification is required
        throw err;
      }

      // Check if this is an /auth/me call or payment-links call - these shouldn't trigger retry
      const url = typeof input === "string" ? input : input?.url || "";
      const isAuthMeCall =
        url.includes("/auth/me") || url.includes("/users/me");
      const isPaymentLinksCall = url.includes("/payment-links");
      // Don't intercept login/register - let the error bubble up to the form
      const isAuthLoginCall =
        url.includes("/auth/login") || url.includes("/auth/register");

      if (
        status === 401 &&
        !isAuthMeCall &&
        !isPaymentLinksCall &&
        !isAuthLoginCall &&
        !credentialsOmitted
      ) {
        // With Kratos, we don't have a refresh endpoint - session cookies handle auth
        // If we get 401, it means the session is invalid, so show auth modal or redirect
        const route = nuxt.$router?.currentRoute?.value;
        // Use window.location.pathname as a reliable fallback for the route path.
        // During early hydration the Vue Router may not have settled yet, so
        // route?.path can be undefined even though the URL is correct. Without
        // the fallback isBookingPage would evaluate to false and the user would
        // be incorrectly redirected to "/" instead of seeing the auth modal.
        const currentPath =
          route?.path ||
          (typeof window !== "undefined" ? window.location.pathname : "");
        const isBookingPage = currentPath.startsWith("/booking");

        if (isBookingPage) {
          // Open auth modal instead of navigating away
          const { openAuthModal } = useAuthModal();
          openAuthModal("signin", route?.fullPath || currentPath);
        } else {
          // For other pages, navigate to home (not admin signin!)
          try {
            navigateTo("/");
          } catch (e) {}
        }
      }
      throw err;
    }
  };

  // Patch available $fetch references to our wrapper when possible
  try {
    (globalThis as any).$fetch = wrapper;
  } catch (e) {}
  try {
    (nuxt as any).$fetch = wrapper;
  } catch (e) {}

  return { provide: { $fetch: wrapper } };
});
